import { PureComponent, type ReactNode } from 'react';

import Button from '../../../ui/button/button';
import type { FormFieldEvent } from '../../../ui/forms/types';
import TextField from '../../../ui/input_field/input_field';
import generatePassword from '../../../utils/generate_password';
import CopyButton from '../../copy_button/copy_button';
import TooltipTrigger from '../../tooltip_trigger/tooltip_trigger';

import styles from './password_generator.module.css';

export type ValueType = string | undefined;

type PasswordGeneratorProps = {
  id?: string;
  disabled?: boolean;
  name?: string;
  value?: ValueType;
  onChange?: (value: ValueType, event?: FormFieldEvent) => void;
  onBlur?: (value: ValueType, event: FormFieldEvent) => void;
  autoFocus?: boolean;
  displayGeneratorInField?: boolean;
  displayCopyIcon?: boolean;
};

type State = {
  password?: string;
};

class PasswordGenerator extends PureComponent<PasswordGeneratorProps, State> {
  state: State = { password: this.props.value };

  componentDidUpdate(prevProps: Readonly<PasswordGeneratorProps>): void {
    if (prevProps.value !== this.props.value) {
      this.setState({ password: this.props.value });
    }
  }

  onGeneratePassword = (): void => {
    const password = generatePassword();
    this.onChange(password);
  };

  onChange = (value: ValueType, event?: FormFieldEvent): void => {
    this.props.onChange?.(value, event);
    this.setState({ password: value });
  };

  handleCopyButtonClick = (): void => {
    if (!this.props.value) {
      event?.stopPropagation();
    }
  };

  render(): ReactNode {
    return (
      <div className={styles.root}>
        <TextField
          {...this.props}
          className={styles.password_input}
          onChange={this.onChange}
          onBlur={this.props.onBlur}
          canClear
          value={this.state.password}
          canGeneratePassword={this.props.displayGeneratorInField}
          onGeneratePassword={this.onGeneratePassword}
        />
        {!this.props.displayGeneratorInField && (
          <TooltipTrigger
            tooltipText="Generate password"
            componentProps={{
              className: styles.generate_button_tooltip,
            }}
          >
            <Button
              className={styles.generate_button}
              onClick={this.onGeneratePassword}
              noBorder
              iconName="refresh"
              wideBasis={false}
              aria={{ label: 'Generate password' }}
            />
          </TooltipTrigger>
        )}
        {this.props.displayCopyIcon && (
          <CopyButton
            className={styles.copy_button}
            onClick={this.handleCopyButtonClick}
            copy={this.props.value || ''}
            placement="top"
          />
        )}
      </div>
    );
  }
}

export default PasswordGenerator;
